/* =========================================================
   EUROHUB GLOBAL QUICK CART
   Kalles + Shopify Ajax Cart
   ========================================================= */

document.addEventListener('DOMContentLoaded', initEuroHubQuickCart);

let eurohubCurrentCart = null;
let eurohubObserverTimer = null;
let eurohubSyncTimer = null;
let eurohubSyncBusy = false;


/* =========================================================
   INIT
   ========================================================= */

async function initEuroHubQuickCart() {
  console.log('[EuroHub Quick Cart] Loaded');

  try {
    normalizeAllProductCards();
    setupProductPageAdapter();

    await refreshEuroHubCart();

    setupEuroHubObserver();

    window.EuroHubQuickCartSync =
      refreshEuroHubCart;

  } catch (error) {
    console.error(
      '[EuroHub Quick Cart] Init failed:',
      error
    );
  }
}


/* =========================================================
   SHOPIFY CART
   ========================================================= */

async function getCart() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json'
    },
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error(
      'Unable to load Shopify cart'
    );
  }

  return response.json();
}


function getCartMap(cart) {
  const map = {};

  if (
    !cart ||
    !Array.isArray(cart.items)
  ) {
    return map;
  }

  cart.items.forEach(function (item) {
    const variantId =
      String(item.variant_id);

    if (!map[variantId]) {
      map[variantId] = {
        quantity: 0,
        key: item.key
      };
    }

    map[variantId].quantity +=
      Number(item.quantity || 0);

    map[variantId].key =
      item.key;
  });

  return map;
}


/* =========================================================
   PRODUCT CARD TITLE + CURRENT PRICE
   ========================================================= */

function normalizeProductTitlePrice(
  card,
  info
) {
  if (!card || !info) {
    return null;
  }

  const title =
    info.querySelector(
      '.t4s-product-title'
    );

  const price =
    info.querySelector(
      '.t4s-product-price'
    );

  if (!title) {
    return null;
  }

  let wrapper =
    title.closest(
      '.eurohub-title-price'
    );

  if (!wrapper) {
    wrapper =
      document.createElement('div');

    wrapper.className =
      'eurohub-title-price';

    title.parentNode.insertBefore(
      wrapper,
      title
    );

    wrapper.appendChild(title);
  }

  /*
    Read current selling price from the
    original Kalles price element.

    Product-page price is NOT affected,
    because this function only processes
    product-card .t4s-product-price.
  */

  if (price) {
    const currentPrice =
      getCurrentProductCardPrice(price);

    let currentPriceElement =
      wrapper.querySelector(
        '.eurohub-current-price'
      );

    if (
      currentPrice &&
      !currentPriceElement
    ) {
      currentPriceElement =
        document.createElement('span');

      currentPriceElement.className =
        'eurohub-current-price';

      wrapper.appendChild(
        currentPriceElement
      );
    }

    if (
      currentPriceElement &&
      currentPrice
    ) {
      currentPriceElement.textContent =
        currentPrice;
    }

    /*
      Original Kalles price remains in DOM
      for theme scripts, but does not occupy
      visual space on product cards.
    */

    price.classList.add(
      'eurohub-source-price'
    );

    /*
      Measure actual current-price width so
      the second title line automatically
      leaves exactly enough room for price.
    */

    if (currentPriceElement) {
      requestAnimationFrame(function () {
        const priceWidth =
          Math.ceil(
            currentPriceElement
              .getBoundingClientRect()
              .width
          );

        wrapper.style.setProperty(
          '--eurohub-price-space',
          (priceWidth + 12) + 'px'
        );
      });
    }
  }

  return wrapper;
}


/* =========================================================
   FIND CURRENT SELLING PRICE
   ========================================================= */

function getCurrentProductCardPrice(
  priceElement
) {
  if (!priceElement) {
    return '';
  }

  /*
    Prefer common sale/current-price elements.
  */

  const preferredSelectors = [
    'ins',
    '.t4s-price-sale',
    '.t4s-sale-price',
    '.t4s-price__sale',
    '.price-item--sale',
    '[data-sale-price]'
  ];

  for (
    let i = 0;
    i < preferredSelectors.length;
    i++
  ) {
    const elements =
      priceElement.querySelectorAll(
        preferredSelectors[i]
      );

    if (elements.length) {
      const text =
        elements[
          elements.length - 1
        ].textContent
          .replace(/\s+/g, ' ')
          .trim();

      if (text) {
        return text;
      }
    }
  }

  /*
    Otherwise read all displayed currency
    amounts and use the LAST amount.

    Example:
    S$69.00 S$55.00
    -> S$55.00
  */

  const rawText =
    priceElement.textContent
      .replace(/\s+/g, ' ')
      .trim();

  const moneyMatches =
    rawText.match(
      /(?:S\$|SGD\s*\$?|US\$|A\$|€|£|\$)\s*\d[\d,]*(?:\.\d{1,2})?/gi
    );

  if (
    moneyMatches &&
    moneyMatches.length
  ) {
    return moneyMatches[
      moneyMatches.length - 1
    ]
      .replace(/\s+/g, '');
  }

  return rawText;
}


/* =========================================================
   NORMALIZE ALL PRODUCT CARDS
   ========================================================= */

function normalizeAllProductCards() {
  document
    .querySelectorAll('.t4s-product')
    .forEach(normalizeProductCard);
}


function normalizeProductCard(card) {
  if (!card) return;

  const info =
    card.querySelector(
      '.t4s-product-info__inner'
    ) ||
    card.querySelector(
      '.t4s-product-info'
    );

  if (!info) return;

  /*
    First normalize title + price.
  */

  const titlePriceWrapper =
    normalizeProductTitlePrice(
      card,
      info
    );

  /*
    Find or create EuroHub buy slot.
  */

  let slot =
    Array.from(
      info.children
    ).find(function (element) {
      return (
        element.classList &&
        element.classList.contains(
          'eurohub-buy-slot'
        )
      );
    });

  if (!slot) {
    slot =
      document.createElement('div');

    slot.className =
      'eurohub-buy-slot';

    const price =
      info.querySelector(
        '.t4s-product-price'
      );

    if (
      price &&
      price.parentNode === info
    ) {
      price.insertAdjacentElement(
        'afterend',
        slot
      );

    } else if (
      titlePriceWrapper &&
      titlePriceWrapper.parentNode ===
        info
    ) {
      titlePriceWrapper
        .insertAdjacentElement(
          'afterend',
          slot
        );

    } else {
      info.appendChild(slot);
    }
  }

  /*
    Find Add to Cart buttons.
  */

  const addButtons =
    Array.from(
      card.querySelectorAll(
        '.t4s-pr-addtocart[data-variant-id]'
      )
    );

  /*
    No Add to Cart:
    Sold out / Read more / etc.
  */

  if (addButtons.length === 0) {
    return;
  }

  let primaryButton =
    slot.querySelector(
      '.t4s-pr-addtocart[data-variant-id]'
    );

  if (!primaryButton) {
    primaryButton =
      info.querySelector(
        '.t4s-product-btns ' +
        '.t4s-pr-addtocart[data-variant-id]'
      );
  }

  if (!primaryButton) {
    primaryButton =
      addButtons[0];
  }

  if (!primaryButton) {
    return;
  }

  primaryButton.dataset
    .eurohubPrimaryAtc =
    'true';

  /*
    Move actual Kalles Add-to-Cart element
    so Kalles event handling is preserved.
  */

  if (
    primaryButton.parentNode !==
    slot
  ) {
    slot.appendChild(
      primaryButton
    );
  }

  /*
    Hide duplicate Add-to-Cart buttons only.
    Quick View / Wishlist remain untouched.
  */

  addButtons.forEach(function (button) {
    if (
      button !== primaryButton
    ) {
      button.dataset
        .eurohubDuplicateAtc =
        'true';

      button.style.setProperty(
        'display',
        'none',
        'important'
      );
    }
  });

  /*
    Keep only one EuroHub quantity control.
  */

  const controls =
    Array.from(
      card.querySelectorAll(
        '.eurohub-qty-control'
      )
    );

  let primaryControl =
    slot.querySelector(
      '.eurohub-qty-control'
    );

  if (
    !primaryControl &&
    controls.length > 0
  ) {
    primaryControl =
      controls[0];

    slot.appendChild(
      primaryControl
    );
  }

  controls.forEach(function (control) {
    if (
      primaryControl &&
      control !== primaryControl
    ) {
      control.remove();
    }
  });
}


/* =========================================================
   SYNC PRODUCT CARDS
   ========================================================= */

function syncProductCards(cart) {
  normalizeAllProductCards();

  const map =
    getCartMap(cart);

  document
    .querySelectorAll('.t4s-product')
    .forEach(function (card) {
      const slot =
        card.querySelector(
          '.eurohub-buy-slot'
        );

      if (!slot) return;

      const addButton =
        slot.querySelector(
          '.t4s-pr-addtocart' +
          '[data-variant-id]'
        );

      const existingControl =
        slot.querySelector(
          '.eurohub-qty-control' +
          '[data-variant-id]'
        );

      let variantId = null;

      if (addButton) {
        variantId =
          String(
            addButton.dataset.variantId
          );

      } else if (
        existingControl
      ) {
        variantId =
          String(
            existingControl
              .dataset.variantId
          );
      }

      if (!variantId) return;

      const cartItem =
        map[variantId];

      const quantity =
        cartItem
          ? cartItem.quantity
          : 0;

      if (quantity > 0) {
        if (addButton) {
          addButton.style.setProperty(
            'display',
            'none',
            'important'
          );
        }

        renderQuantityControl(
          slot,
          quantity,
          variantId,
          cartItem.key
        );

      } else {
        if (existingControl) {
          existingControl.remove();
        }

        if (addButton) {
          addButton.style
            .removeProperty(
              'display'
            );
        }
      }
    });
}


/* =========================================================
   PRODUCT PAGE
   ========================================================= */

function setupProductPageAdapter() {
  normalizeProductPage();

  document.addEventListener(
    'change',
    function (event) {
      const form =
        event.target.closest(
          '.t4s-form__product'
        );

      if (!form) return;

      scheduleEuroHubSync(180);
    }
  );
}


function normalizeProductPage() {
  const forms =
    document.querySelectorAll(
      '.t4s-form__product' +
      '[action*="/cart/add"]'
    );

  forms.forEach(function (form) {
    const buttonsWrap =
      form.querySelector(
        '.t4s-product-form__buttons'
      );

    const submitButton =
      form.querySelector(
        '.t4s-product-form__submit' +
        '[data-atc-form], ' +
        '.t4s-product-form__submit' +
        '[name="add"]'
      );

    const nativeQty =
      form.querySelector(
        '.t4s-product-form__qty' +
        '[data-quantity-wrapper], ' +
        '.t4s-product-form__qty'
      );

    if (
      !buttonsWrap ||
      !submitButton
    ) {
      return;
    }

    let slot =
      buttonsWrap.querySelector(
        '.eurohub-product-page-slot'
      );

    if (!slot) {
      slot =
        document.createElement('div');

      slot.className =
        'eurohub-product-page-slot';

      buttonsWrap.appendChild(slot);
    }

    form.dataset
      .eurohubProductPage =
      'true';

    if (nativeQty) {
      nativeQty.dataset
        .eurohubNativeQty =
        'true';
    }

    submitButton.dataset
      .eurohubProductPageAtc =
      'true';
  });
}


function getProductPageVariantId(form) {
  if (!form) return null;

  const input =
    form.querySelector(
      'input[name="id"]'
    );

  if (
    !input ||
    !input.value
  ) {
    return null;
  }

  return String(input.value);
}


function syncProductPage(cart) {
  normalizeProductPage();

  const map =
    getCartMap(cart);

  document
    .querySelectorAll(
      '.t4s-form__product' +
      '[data-eurohub-product-page="true"]'
    )
    .forEach(function (form) {
      const variantId =
        getProductPageVariantId(
          form
        );

      if (!variantId) return;

      const item =
        map[variantId];

      const quantity =
        item
          ? item.quantity
          : 0;

      const submitButton =
        form.querySelector(
          '.t4s-product-form__submit' +
          '[data-eurohub-product-page-atc="true"]'
        );

      const nativeQty =
        form.querySelector(
          '[data-eurohub-native-qty="true"]'
        );

      const slot =
        form.querySelector(
          '.eurohub-product-page-slot'
        );

      if (
        !submitButton ||
        !slot
      ) {
        return;
      }

      if (quantity > 0) {
        submitButton.style.setProperty(
          'display',
          'none',
          'important'
        );

        if (nativeQty) {
          nativeQty.style.setProperty(
            'display',
            'none',
            'important'
          );
        }

        slot.style.removeProperty(
          'display'
        );

        renderQuantityControl(
          slot,
          quantity,
          variantId,
          item.key
        );

      } else {
        const control =
          slot.querySelector(
            '.eurohub-qty-control'
          );

        if (control) {
          control.remove();
        }

        slot.style.setProperty(
          'display',
          'none',
          'important'
        );

        submitButton.style
          .removeProperty(
            'display'
          );

        if (nativeQty) {
          nativeQty.style.setProperty(
            'display',
            'none',
            'important'
          );
        }
      }
    });
}


/* =========================================================
   SHARED QUANTITY CONTROL
   ========================================================= */

function renderQuantityControl(
  slot,
  quantity,
  variantId,
  lineKey
) {
  if (!slot) return;

  let control =
    slot.querySelector(
      '.eurohub-qty-control'
    );

  if (control) {
    const number =
      control.querySelector(
        '.eurohub-qty-number'
      );

    if (number) {
      number.textContent =
        quantity;
    }

    control.dataset.variantId =
      String(variantId);

    control.dataset.lineKey =
      lineKey || '';

    return;
  }

  control =
    document.createElement('div');

  control.className =
    'eurohub-qty-control';

  control.dataset.variantId =
    String(variantId);

  control.dataset.lineKey =
    lineKey || '';

  control.dataset.busy =
    'false';

  control.innerHTML = `
    <button
      type="button"
      class="eurohub-qty-btn eurohub-qty-minus"
      aria-label="Decrease quantity"
    >−</button>

    <span
      class="eurohub-qty-number"
      aria-live="polite"
    >${quantity}</span>

    <button
      type="button"
      class="eurohub-qty-btn eurohub-qty-plus"
      aria-label="Increase quantity"
    >+</button>
  `;

  slot.appendChild(control);
}


/* =========================================================
   REFRESH ALL FROM SHOPIFY
   ========================================================= */

async function refreshEuroHubCart() {
  if (eurohubSyncBusy) {
    return eurohubCurrentCart;
  }

  eurohubSyncBusy = true;

  try {
    normalizeAllProductCards();
    normalizeProductPage();

    const cart =
      await getCart();

    eurohubCurrentCart =
      cart;

    syncProductCards(cart);
    syncProductPage(cart);
    syncHeaderCart(cart);
    syncExistingMiniCart(cart);

    console.log(
      '[EuroHub Quick Cart] Synced:',
      cart.item_count
    );

    return cart;

  } catch (error) {
    console.error(
      '[EuroHub Quick Cart] Sync failed:',
      error
    );

    return null;

  } finally {
    eurohubSyncBusy = false;
  }
}


/* =========================================================
   PLUS / MINUS
   ========================================================= */

document.addEventListener(
  'click',
  async function (event) {
    const plus =
      event.target.closest(
        '.eurohub-qty-plus'
      );

    const minus =
      event.target.closest(
        '.eurohub-qty-minus'
      );

    if (!plus && !minus) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const control =
      event.target.closest(
        '.eurohub-qty-control'
      );

    if (
      !control ||
      control.dataset.busy ===
        'true'
    ) {
      return;
    }

    const qtyElement =
      control.querySelector(
        '.eurohub-qty-number'
      );

    const currentQty =
      Number(
        qtyElement
          ? qtyElement.textContent
          : 0
      ) || 0;

    const newQty =
      plus
        ? currentQty + 1
        : Math.max(
            0,
            currentQty - 1
          );

    await changeQuantity(
      control,
      newQty
    );
  },
  true
);


async function changeQuantity(
  control,
  newQuantity
) {
  if (
    !control ||
    control.dataset.busy ===
      'true'
  ) {
    return;
  }

  control.dataset.busy =
    'true';

  setControlLoading(
    control,
    true
  );

  try {
    const freshCart =
      await getCart();

    const variantId =
      String(
        control.dataset.variantId
      );

    const map =
      getCartMap(freshCart);

    const item =
      map[variantId];

    if (
      !item ||
      !item.key
    ) {
      throw new Error(
        'Cart line could not be found'
      );
    }

    const response =
      await fetch(
        '/cart/change.js',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },

          body: JSON.stringify({
            id: item.key,
            quantity: newQuantity
          })
        }
      );

    const cart =
      await response.json();

    if (!response.ok) {
      throw new Error(
        cart.description ||
        cart.message ||
        'Unable to update cart'
      );
    }

    eurohubCurrentCart =
      cart;

    syncProductCards(cart);
    syncProductPage(cart);
    syncHeaderCart(cart);
    syncExistingMiniCart(cart);

    console.log(
      '[EuroHub Quick Cart] Quantity:',
      variantId,
      newQuantity
    );

  } catch (error) {
    console.error(
      '[EuroHub Quick Cart] Update failed:',
      error
    );

    alert(
      'Unable to update quantity. Please try again.'
    );

    scheduleEuroHubSync(150);

  } finally {
    if (
      document.body.contains(
        control
      )
    ) {
      control.dataset.busy =
        'false';

      setControlLoading(
        control,
        false
      );
    }
  }
}


/* =========================================================
   FIRST ADD — LET KALLES HANDLE IT
   ========================================================= */

document.addEventListener(
  'click',
  function (event) {
    const collectionAddButton =
      event.target.closest(
        '.t4s-pr-addtocart' +
        '[data-variant-id]'
      );

    const productPageAddButton =
      event.target.closest(
        '.t4s-product-form__submit' +
        '[data-eurohub-product-page-atc="true"]'
      );

    if (
      !collectionAddButton &&
      !productPageAddButton
    ) {
      return;
    }

    if (
      collectionAddButton &&
      collectionAddButton.dataset
        .eurohubDuplicateAtc ===
        'true'
    ) {
      return;
    }

    let variantId = null;

    if (collectionAddButton) {
      variantId =
        String(
          collectionAddButton
            .dataset.variantId || ''
        );

    } else {
      const form =
        productPageAddButton.closest(
          '.t4s-form__product'
        );

      variantId =
        getProductPageVariantId(
          form
        );
    }

    if (!variantId) return;

    waitForKallesAdd(
      variantId,
      0,
      15
    );
  },
  false
);


async function waitForKallesAdd(
  variantId,
  attempt,
  maxAttempts
) {
  await delay(200);

  try {
    const cart =
      await getCart();

    const map =
      getCartMap(cart);

    const item =
      map[String(variantId)];

    if (
      item &&
      item.quantity > 0
    ) {
      eurohubCurrentCart =
        cart;

      syncProductCards(cart);
      syncProductPage(cart);
      syncHeaderCart(cart);

      console.log(
        '[EuroHub Quick Cart] Kalles add detected:',
        variantId
      );

      return;
    }

  } catch (error) {
    console.warn(
      '[EuroHub Quick Cart] Waiting for Kalles:',
      error
    );
  }

  if (
    attempt + 1 <
    maxAttempts
  ) {
    waitForKallesAdd(
      variantId,
      attempt + 1,
      maxAttempts
    );
  }
}


/* =========================================================
   HEADER CART
   ========================================================= */

function syncHeaderCart(cart) {
  document
    .querySelectorAll(
      '[data-cart-count]'
    )
    .forEach(function (element) {
      element.textContent =
        cart.item_count;

      if (
        cart.item_count > 0
      ) {
        element.classList.remove(
          't4s-op-0'
        );

      } else {
        element.classList.add(
          't4s-op-0'
        );
      }
    });

  document
    .querySelectorAll(
      '[data-cart-tt-price]'
    )
    .forEach(function (element) {
      element.textContent =
        formatCartMoney(
          cart.total_price,
          cart.currency
        );
    });
}


/* =========================================================
   EXISTING MINI CART
   ========================================================= */

function syncExistingMiniCart(cart) {
  const drawer =
    document.querySelector(
      '#t4s-mini_cart'
    );

  if (!drawer) return;

  drawer.setAttribute(
    'data-ccount',
    String(cart.item_count)
  );

  const cartKeys =
    new Map();

  cart.items.forEach(function (item) {
    cartKeys.set(
      item.key,
      item
    );
  });

  drawer
    .querySelectorAll(
      '.t4s-quantity-input[data-id]'
    )
    .forEach(function (input) {
      const key =
        input.dataset.id;

      const item =
        cartKeys.get(key);

      if (item) {
        input.value =
          item.quantity;

        input.setAttribute(
          'value',
          String(item.quantity)
        );

        input.dataset.currentQty =
          String(item.quantity);

      } else {
        const row =
          input.closest(
            '[data-cart-item]'
          );

        if (row) {
          row.remove();
        }
      }
    });

  drawer
    .querySelectorAll(
      '.t4s-cart__totalPrice'
    )
    .forEach(function (element) {
      element.textContent =
        formatCartMoney(
          cart.total_price,
          cart.currency
        );
    });
}


/* =========================================================
   DRAWER -> GLOBAL SYNC
   ========================================================= */

document.addEventListener(
  'change',
  function (event) {
    const input =
      event.target.closest(
        '#t4s-mini_cart ' +
        '.t4s-quantity-input'
      );

    if (!input) return;

    scheduleEuroHubSync(400);
  },
  false
);


document.addEventListener(
  'click',
  function (event) {
    const drawer =
      event.target.closest(
        '#t4s-mini_cart'
      );

    if (!drawer) return;

    const quantityAction =
      event.target.closest(
        '[data-quantity-wrapper] button'
      );

    const removeAction =
      event.target.closest(
        '[data-remove], ' +
        '[data-cart-remove], ' +
        '.t4s-mini_cart__remove, ' +
        '.t4s-mini_cart__remove-item'
      );

    if (
      quantityAction ||
      removeAction
    ) {
      scheduleEuroHubSync(500);
    }
  },
  false
);


/* =========================================================
   PAGE RETURN / TAB SYNC
   ========================================================= */

window.addEventListener(
  'pageshow',
  function () {
    scheduleEuroHubSync(100);
  }
);


document.addEventListener(
  'visibilitychange',
  function () {
    if (
      document.visibilityState ===
      'visible'
    ) {
      scheduleEuroHubSync(150);
    }
  }
);


window.addEventListener(
  'focus',
  function () {
    scheduleEuroHubSync(150);
  }
);


/* =========================================================
   HELPERS
   ========================================================= */

function setControlLoading(
  control,
  loading
) {
  if (!control) return;

  control
    .querySelectorAll('button')
    .forEach(function (button) {
      button.disabled = loading;
    });

  control.style.opacity =
    loading ? '0.55' : '1';
}


function formatCartMoney(
  amount,
  currency
) {
  const value =
    Number(amount || 0) / 100;

  if (
    !currency ||
    currency === 'SGD'
  ) {
    return (
      'S$' +
      value.toFixed(2)
    );
  }

  try {
    return new Intl.NumberFormat(
      document.documentElement.lang ||
      'en-SG',
      {
        style: 'currency',
        currency: currency
      }
    ).format(value);

  } catch (error) {
    return (
      currency +
      ' ' +
      value.toFixed(2)
    );
  }
}


function scheduleEuroHubSync(
  delayMs
) {
  clearTimeout(
    eurohubSyncTimer
  );

  eurohubSyncTimer =
    setTimeout(
      function () {
        refreshEuroHubCart();
      },
      delayMs || 200
    );
}


function delay(ms) {
  return new Promise(
    function (resolve) {
      setTimeout(
        resolve,
        ms
      );
    }
  );
}


/* =========================================================
   KALLES DYNAMIC CONTENT
   ========================================================= */

function setupEuroHubObserver() {
  const observer =
    new MutationObserver(
      function (mutations) {
        if (
          !eurohubCurrentCart
        ) {
          return;
        }

        let hasNewContent =
          false;

        mutations.forEach(
          function (mutation) {
            if (
              mutation.addedNodes &&
              mutation.addedNodes.length > 0
            ) {
              hasNewContent =
                true;
            }
          }
        );

        if (!hasNewContent) {
          return;
        }

        clearTimeout(
          eurohubObserverTimer
        );

        eurohubObserverTimer =
          setTimeout(
            function () {
              normalizeAllProductCards();
              normalizeProductPage();

              syncProductCards(
                eurohubCurrentCart
              );

              syncProductPage(
                eurohubCurrentCart
              );
            },
            120
          );
      }
    );

  observer.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );
}